// Application constants
export const APP_NAME = "FantasyJolt";
export const APP_DESCRIPTION = "Fantasy racing platform for NASCAR enthusiasts";

// Pagination defaults
export const DEFAULT_PAGE_SIZE = 20;
export const MAX_PAGE_SIZE = 100;

// Contest constants
export const MIN_CONTEST_CAPACITY = 2;
export const MAX_CONTEST_CAPACITY = 10000;
export const MIN_ENTRY_FEE = 0;
export const MIN_PAID_CONTEST_ENTRY_FEE_COINS = 50;
export const MAX_ENTRY_FEE = 10000;
export const RAKE_PERCENTAGE = 0.1; // 10%

export function normalizePaidContestEntryFee(entryFee: number) {
  return entryFee > 0 && entryFee < MIN_PAID_CONTEST_ENTRY_FEE_COINS
    ? MIN_PAID_CONTEST_ENTRY_FEE_COINS
    : entryFee;
}

// Lineup constants
export const LINEUP_SIZE = 5;
export const SALARY_CAP = 50000;
export const LINEUP_POSITIONS = ["A", "B", "C", "D", "E"] as const;

// Scoring constants
export const DEFAULT_SCORING = {
  START: 0, // Points per start position
  FINISH: {
    1: 45,
    2: 42,
    3: 41,
    4: 40,
    5: 39,
    6: 38,
    7: 37,
    8: 36,
    9: 35,
    10: 34,
  },
  LAPS_LED: 0.25, // Points per lap led
  FASTEST_LAPS: 0.5, // Points per fastest lap
  PLACE_DIFFERENTIAL: 0.5, // Points per position gained
  STAGE_WIN: 10, // Points for stage win
} as const;

// Track types
export const TRACK_TYPES = [
  "Superspeedway",
  "Intermediate",
  "Short Track",
  "Road Course",
] as const;

// Contest types
export const CONTEST_TYPES = [
  "Winner Take All",
  "Top 3 Pays",
  "Top 10 Pays",
  "50/50",
] as const;

// Contest statuses
// - pending: Contest created, users can reserve spots (no lineup submission yet)
// - locked: Projections uploaded, users can enter/edit lineups
// - live: Race in progress, no more edits
// - completed: Race finished
// - cancelled: Contest cancelled
export const CONTEST_STATUSES = [
  "pending",
  "locked",
  "live",
  "completed",
  "cancelled",
] as const;

// Race statuses (for schedules table)
export const RACE_STATUSES = [
  "pending",
  "locked",
  "live",
  "completed",
] as const;

// Transaction types (coin-based)
export const TRANSACTION_TYPES = [
  "contest_entry",
  "contest_win",
  "top_3_finish",
  "top_10_finish",
  "daily_login",
  "referral_bonus",
  "achievement",
  "admin_grant",
] as const;

// User statuses
export const USER_STATUSES = ["active", "suspended", "banned"] as const;

// League roles
export const LEAGUE_ROLES = ["member", "admin", "moderator"] as const;

// Bot profiles
export const BOT_PROFILES = ["whale", "small_player", "regular"] as const;

// NASCAR manufacturers
export const MANUFACTURERS = ["Chevrolet", "Ford", "Toyota"] as const;

// Scoring categories
export const SCORING_CATEGORIES = [
  "START",
  "FINISH",
  "FASTEST_LAP",
  "FASTEST_LAPS",
  "LAPS_LED",
  "PLACE_DIFFERENTIAL",
  "LAPS_COMPLETED",
  "STAGE1",
  "STAGE2",
  "STAGE3",
] as const;

// Default stage points (1st=10, 2nd=9, ... 10th=1) - for positions 1-10
export const DEFAULT_STAGE_POINTS_SCALE: Record<number, number> = {
  1: 10, 2: 9, 3: 8, 4: 7, 5: 6, 6: 5, 7: 4, 8: 3, 9: 2, 10: 1,
};


// Session constants
export const SESSION_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds
export const IMPERSONATION_MAX_AGE = 24 * 60 * 60; // 24 hours in seconds

// Race lock time (minutes before race start) — legacy, unused
export const RACE_LOCK_MINUTES = 30;

/**
 * Lineup-lock buffer: how long AFTER the scheduled "TV start" time we
 * still allow lineup submission/edits. NASCAR's broadcast goes live at
 * the scheduled time, but the green flag drops 8-15 min later (anthem,
 * intros, command, parade laps).
 *
 * COMPETITIVE INTEGRITY PRIORITY (per Mark, 5/3 Cup Texas): we'd rather
 * lock TOO EARLY than risk a single user editing their lineup after the
 * green flag drops — lineup edits during a race are an unfair advantage
 * that no amount of "slightly more edit time" justifies. Set to 5 min:
 * comfortably past the scheduled TV start (so quick TV delays don't
 * close the window prematurely) but well before even the fastest
 * pre-race ceremonies finish. DK uses 10 min; we deliberately go
 * shorter as a safety margin until live-feed flag_state polling lands
 * and we can lock at the actual green flag drop.
 *
 * Used for:
 *   - Backend: assertRaceNotStarted + runContestTransitions cutoffs
 *   - Frontend: countdown displays (count down to startTime + buffer)
 */
export const LINEUP_LOCK_BUFFER_MINUTES = 5;
export const LINEUP_LOCK_BUFFER_MS = LINEUP_LOCK_BUFFER_MINUTES * 60 * 1000;

// API rate limits
export const RATE_LIMITS = {
  GENERAL: 100, // requests per minute
  AUTH: 10, // auth attempts per minute
  LINEUP_SUBMIT: 20, // lineup submissions per minute
} as const;

// DFS Scoring Systems
export * from "./scoring-systems";

// Lineup Presets
export * from "./lineup-presets";

// Finish Position Points Presets
export * from "./finish-points-presets";

// Season Pass Prorated Pricing
export * from "./season-pass-pricing";

// Contest Name Prefixes
export * from "./contest-names";

// Series Configuration
export * from "./series";
